import PodcastCategoryDropdown from 'components/molecules/PodcastCategoryDropDown';
import { notifier } from 'components/Toaster';
import useCategory from 'hooks/API/GET/useCategory';
import useCategoryDropdown from 'hooks/API/GET/useCategoryDropdown';
import useGetOnePodcast from 'hooks/API/GET/useGetOnePodcast';
import useManagePodcast from 'hooks/API/POST/useManagePodcast';
import Joi from 'joi';
import { useHistory, useParams, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { checkForErrors } from 'utilities/fns';
import { Link } from 'react-router-dom';
import * as AppPics from 'assets/images';
import { useState, useEffect, useRef } from 'react';
import { useQueryClient } from 'react-query';
import AppBreadcrumbs from 'components/molecules/AppBreadcrumbs';
import BasicDelete from 'components/molecules/Modal/Delete/BasicDelete';

const actionsConfig = {
  open: false,
  data: {},
  Header: null,
  Body: null,
  isDeleteOperation: false,
};

const { AppTextField, AppRadioBox, AppUploadBlock, AppButton } = require('components');

const initialPodcastState = {
  podcastName: '',
  podcastSubtitle: '',
  podcastArtist: '',

  podcastUrl: null,
  section: '',
  podcastAlbumImage: '',
  podcastAlbumImageFullPath: '',
  podcastImage: '',
  podcastImageFullPath: '',
  podcastCategory: '',
  canPublish: null,
};

function ManagePodcast() {
  const { podcastId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const queryClient = useQueryClient();
  const dropdownRef = useRef(null);

  const params = new URLSearchParams(location.search);
  const redirectBack = params.get('redirect_back');

  const [podcastInfo, setPodcastInfo] = useState({ ...initialPodcastState });
  const [formErrors, setFormErrors] = useState([]);
  const [validationErrorServer, setValidationErrorServer] = useState({});
  const [categories, setCategories] = useState([]);
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [newlyUploadedImages, setNewlyUploadedImages] = useState({
    albumImage: false,
    podcastImage: false,
  });
  const [deletedImages, setDeletedImages] = useState({
    albumImage: false,
    podcastImage: false,
  });
  const [actions, setActions] = useState({ ...actionsConfig });
  const [published, setPublished] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [genreId, setGenreId] = useState('');

  const {
    data: categoriesData,
    isLoading: isCategoryLoading,
    isError: isCategoryError,
  } = useCategoryDropdown({
    enabled: true,
    configs: { params: { mode: 'dropdown' } },
  });

  useEffect(() => {
    if (categoriesData?.data && Array.isArray(categoriesData.data)) {
      const mappedCategories = categoriesData.data.map((item) => ({
        value: item._id || item.value,
        label: item.categoryName || item.label,
      }));
      setCategories(mappedCategories);
    }
  }, [categoriesData]);

  const clearForm = () => {
    setPodcastInfo({ ...initialPodcastState });
    setFormErrors([]);
    setValidationErrorServer({});
    setDisableSubmit(false);
    setNewlyUploadedImages({
      albumImage: false,
      podcastImage: false,
    });
  };

  const handleGoBack = () => {
    if (redirectBack) {
      history.replace(decodeURIComponent(redirectBack));
    } else {
      history.goBack();
    }
  };

  useEffect(() => {
    if (podcastId === 'add') {
      clearForm();
      queryClient.removeQueries(['getSinglePodcast', podcastId]);
      return;
    }

    if (redirectBack && window.history.length <= 2) {
      window.history.replaceState(null, '', location.pathname + location.search);
      window.history.pushState(null, '', decodeURIComponent(redirectBack));
      window.history.pushState(null, '', location.pathname + location.search);
    }
  }, [podcastId, redirectBack, location.pathname, location.search, queryClient]);

  const { isLoading, mutate, isError } = useManagePodcast();

  const getSinglePodcast = useGetOnePodcast({
    enabled: podcastId !== 'add',
    podcastId,
    config: { params: { podcastId } },
  });

  useEffect(() => {
    if (podcastId !== 'add' && getSinglePodcast?.data && getSinglePodcast?.isSuccess) {
      const currentData = getSinglePodcast.data?.data?.data;
      setPodcastInfo({
        podcastName: currentData.name || '',
        podcastSubtitle: currentData.subtitle || '',
        podcastArtist: currentData.artists || '',

        podcastUrl: currentData.podcastUrl || undefined,
        podcastCategory: currentData.podcastCategory?._id || '',
        podcastImage: currentData.podcastImage || '',
        podcastAlbumImage: currentData.podcastAlbumImage || '',
        podcastImageFullPath: currentData.podcastImg || '',
        podcastAlbumImageFullPath: currentData.albumImg || '',
        section: currentData.podcastType || '',
        canPublish: currentData.isPublished || '',
      });
      setDeletedImages({
        albumImage: false,
        podcastImage: false,
      });
      setPublished(currentData.isPublished);
      setIsSaved(true);
    }
  }, [podcastId, getSinglePodcast?.data, getSinglePodcast?.isSuccess]);

  const handleInfoChange = (data = {}) => {
    setPodcastInfo((prev) => ({ ...prev, ...data }));
  };
  const handleSectionChange = (value) => {
    setPodcastInfo((prev) => ({ ...prev, section: value }));
  };

  const handleRadioKeyDown = (e, value) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleSectionChange(value);
    }
  };
  const handleCategoryChange = (e) => {
    setPodcastInfo((p) => ({
      ...p,
      podcastCategory: e.target.value,
    }));
  };

  const handleDropdownKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const selectElement = e.target.querySelector('select') || e.target;
      if (selectElement.tagName === 'SELECT') {
        selectElement.focus();
        selectElement.click();
      }
    }
    if (e.key === 'Tab') {
      return;
    }
  };

  const save = () => {
    setFormErrors([]);
    if (disableSubmit) return;

    const validation = podcastInfoSchema.validate(podcastInfo, {
      abortEarly: false,
      allowUnknown: true,
    });

    if (validation?.error) {
      setFormErrors(validation.error?.details ?? []);
      return;
    }

    const payload = {
      podcastName: podcastInfo.podcastName,
      podcastSubtitle: podcastInfo.podcastSubtitle,
      podcastArtists: podcastInfo.podcastArtist,

      url: podcastInfo.podcastUrl,
      image: deletedImages.podcastImage ? null : podcastInfo.podcastImage,
      albumImage: deletedImages.albumImage ? null : podcastInfo.podcastAlbumImage,
      type: podcastInfo.section,
      categoryPodcast: podcastInfo.podcastCategory,
      ...(podcastId !== 'add' ? { podcastId } : {}),
      deleteAlbumImage: deletedImages.albumImage,
      deletePodcastImage: deletedImages.podcastImage,
      canPublish: podcastInfo.canPublish,
    };

    mutate(payload, {
      onError: (failData) => {
        const errorData = failData?.response?.data;
        setValidationErrorServer(errorData?.errors);
        notifier.error(errorData?.message);
      },
      onSuccess: (response) => {
        notifier.success(response?.data?.data?.message || response?.data?.message);
        // setTimeout(() => {
        //   handleGoBack();
        // }, 2000);
        setGenreId(response?.data?.record);
        setIsSaved(true);
      },
    });
  };

  const handlePublish = (value) => {
    setFormErrors([]);
    if (disableSubmit) return;

    const payload = {
      podcastName: podcastInfo.podcastName,
      podcastSubtitle: podcastInfo.podcastSubtitle,
      podcastArtists: podcastInfo.podcastArtist,

      url: podcastInfo.podcastUrl,
      image: deletedImages.podcastImage ? null : podcastInfo.podcastImage,
      albumImage: deletedImages.albumImage ? null : podcastInfo.podcastAlbumImage,
      type: podcastInfo.section,
      categoryPodcast: podcastInfo.podcastCategory,
      podcastId: podcastId && podcastId !== 'add' ? podcastId : genreId,
      deleteAlbumImage: deletedImages.albumImage,
      deletePodcastImage: deletedImages.podcastImage,
      canPublish: value,
    };

    mutate(payload, {
      onError(failData) {
        const errorData = failData?.response?.data;
        const errorMessage = errorData?.message;
        const validationErrors = errorData?.errors;

        setValidationErrorServer(validationErrors || {});
        notifier.error(errorMessage);
      },

      onSuccess() {
        notifier.success(`Podcast ${value ? 'published' : 'unpublished'} successfully`);
        setTimeout(() => {
          handleGoBack();
        }, 2000);
      },
    });
  };

  const errors = checkForErrors(formErrors);

  // const handleDeleteAlbumImage = () => {
  //   setPodcastInfo((prev) => ({
  //     ...prev,
  //     podcastAlbumImage: null,
  //     podcastAlbumImageFullPath: null,
  //   }));
  //   setNewlyUploadedImages((prev) => ({
  //     ...prev,
  //     albumImage: false,
  //   }));
  //   setDeletedImages((prev) => ({
  //     ...prev,
  //     albumImage: true,
  //   }));
  //   notifier.success('Album image deleted successfully');
  // };

  const handleDeletePodcastImage = () => {
    setPodcastInfo((prev) => ({
      ...prev,
      podcastImage: null,
      podcastImageFullPath: null,
    }));
    setNewlyUploadedImages((prev) => ({
      ...prev,
      podcastImage: false,
    }));
    setDeletedImages((prev) => ({
      ...prev,
      podcastImage: true,
    }));
    notifier.success('Podcast image deleted successfully');
  };

  const handleDeletePodcastImageConfirm = () => {
    setActions({
      open: true,
      Header: 'Delete Image',
      Body: 'Are you sure you want to delete this image?',
      isDeleteOperation: true,
      data: {
        type: 'PODCAST_IMAGE',
      },
    });
  };

  const handlePublishToggleConfirm = (newValue) => {
    setActions({
      open: true,
      Header: 'Confirm Publish Status',
      Body: `Are you sure you want to ${newValue ? 'publish' : 'unpublish'} this podcast?`,
      isDeleteOperation: false,
      data: {
        type: 'PUBLISH_TOGGLE',
        value: newValue,
      },
    });
  };

  const handleActionsConfirm = () => {
    const { type, value } = actions.data || {};
    if (type === 'PODCAST_IMAGE') {
      handleDeletePodcastImage();
    }
    if (type === 'PUBLISH_TOGGLE') {
      handlePublish(value);
    }
    setActions({ ...actionsConfig });
  };

  return (
    <>
      <BasicDelete
        open={actions.open}
        closeModal={() => setActions({ ...actionsConfig })}
        Header={actions.Header}
        Body={actions.Body}
        handleConfirmation={handleActionsConfirm}
      />
      <div className="mainWraper coursetabsection">
        <div className="tab">
          <ul className="tab__menu">
            <li className="back">
              <button className="btn-back" onClick={handleGoBack}>
                <img src={AppPics.BackSvg} alt="Back" />
              </button>
            </li>
          </ul>
          <div
            className="tab__content
        ___adjust___tab___padding"
          >
            <div className="tab__content-actHead">
              <AppBreadcrumbs
                links={[
                  { label: 'Dashboard', to: '/dashboard' },
                  { label: 'Podcast Home', to: '/podcast' },
                  { label: 'Podcasts', to: `/podcast/list` },
                ]}
                current={podcastId === 'add' ? 'Add Podcast' : 'Update Podcast'}
              />
            </div>
            <div className="tab__content-actBody">
              <div className="px-5 py-5">
                <div className="row">
                  <div className="col-6">
                    <h2 className="prime-head p-0 boldtitle">
                      {podcastId !== 'add' ? 'Update Podcast' : 'Create Podcast'}
                    </h2>
                    {validationErrorServer && (
                      <ul className="error-list">
                        {Object.values(validationErrorServer).map((err, idx) => (
                          <li key={idx}>{err.msg}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>

                <div className="row mt-4">
                  <div className="col-lg-4 col-md-6 col-sm-12">
                    <AppTextField
                      labelProps={{ label: 'Podcast Name *', htmlFor: 'podcastName' }}
                      inputProps={{
                        id: 'podcastName',
                        value: podcastInfo.podcastName,
                        placeholder: 'Podcast Name',
                        onChange: (e) =>
                          setPodcastInfo((p) => ({ ...p, podcastName: e.target.value })),
                        autoFocus: true,
                        tabIndex: 1,
                      }}
                      fieldErrors={errors['podcastName']}
                    />
                    {errors.podcastName && (
                      <div className="text-danger">{errors.podcastName.errorMessage}</div>
                    )}
                  </div>

                  <div className="col-lg-4 col-md-6 col-sm-12">
                    <AppTextField
                      labelProps={{ label: 'Podcast Subtitle', htmlFor: 'podcastSubtitle' }}
                      inputProps={{
                        id: 'podcastSubtitle',
                        value: podcastInfo.podcastSubtitle,
                        placeholder: 'Subtitle',
                        onChange: (e) =>
                          setPodcastInfo((p) => ({ ...p, podcastSubtitle: e.target.value })),
                        tabIndex: 2,
                      }}
                    />
                  </div>

                  <div className="col-lg-4 col-md-6 col-sm-12">
                    <AppTextField
                      labelProps={{ label: 'Podcast Artist', htmlFor: 'podcastArtist' }}
                      inputProps={{
                        id: 'podcastArtist',
                        value: podcastInfo.podcastArtist,
                        placeholder: 'Artists',
                        onChange: (e) =>
                          setPodcastInfo((p) => ({ ...p, podcastArtist: e.target.value })),
                        tabIndex: 3,
                      }}
                    />
                  </div>
                </div>

                <div className="row mt-4">
                  <div className="col-lg-4 col-md-6 col-sm-12">
                    <AppTextField
                      labelProps={{ label: 'Podcast URL', htmlFor: 'podcastUrl' }}
                      inputProps={{
                        id: 'podcastUrl',
                        value: podcastInfo.podcastUrl,
                        placeholder: 'Podcast URL',
                        onChange: (e) =>
                          setPodcastInfo((p) => ({ ...p, podcastUrl: e.target.value })),
                        tabIndex: 4,
                      }}
                    />
                  </div>

                  <div className="col-lg-8 col-md-6 col-sm-12">
                    <label className="label d-block">
                      Podcast Section
                      <span>
                        <small className="text-muted">
                          {' '}
                          (Popular Podcasts appear in the Popular Podcasts section on the Explore page. Trending is currently not in use.)
                        </small>
                      </span>
                    </label>
                    <AppRadioBox
                      inputProps={{
                        name: 'section',
                        value: 'trending',
                        checked: podcastInfo.section === 'trending',
                        onChange: (e) => handleSectionChange(e.target.value),
                        onFocus: () => handleSectionChange('trending'),
                        onKeyDown: (e) => handleRadioKeyDown(e, 'trending'),
                        tabIndex: 5,
                      }}
                      labelProps={{ label: 'Trending' }}
                    />
                    <AppRadioBox
                      inputProps={{
                        name: 'section',
                        value: 'popular',
                        checked: podcastInfo.section === 'popular',
                        onChange: (e) => handleSectionChange(e.target.value),
                        onFocus: () => handleSectionChange('popular'),
                        onKeyDown: (e) => handleRadioKeyDown(e, 'popular'),
                        tabIndex: 6,
                      }}
                      labelProps={{ label: 'Popular' }}
                    />
                  </div>
                </div>

                <div className="row mt-4">
                  {/* <div className="col-lg-4 col-md-6 col-sm-12">
                  <AppUploadBlock
                    labelProps={{ label: 'Podcast Album Image' }}
                    props={{
                      referenceKey: 'PODCAST_IMAGE',
                      isImage: true,
                      isPublic: true,
                      uploadCallback: setDisableSubmit,
                      handleChange: ({ file, uploadedData }) => {
                        setPodcastInfo((p) => ({
                          ...p,
                          podcastAlbumImage: uploadedData.file,
                          podcastAlbumImageFullPath: `${uploadedData.basePath}${uploadedData.file}`,
                        }));
                        setNewlyUploadedImages((prev) => ({
                          ...prev,
                          albumImage: true,
                        }));
                        setDeletedImages((prev) => ({
                          ...prev,
                          albumImage: false,
                        }));
                      },
                      fileData: {
                        fullPath: deletedImages.albumImage
                          ? null
                          : podcastInfo.podcastAlbumImageFullPath,
                      },
                      clearFile: deletedImages.albumImage,
                      tabIndex: 7,
                    }}
                  />
                  {(podcastInfo.podcastAlbumImage || podcastInfo.podcastAlbumImageFullPath) &&
                    !deletedImages.albumImage && (
                      <button
                        className="actions-icons mx-2 text-danger mt-2"
                        title="Delete Album Image"
                        onClick={handleDeleteAlbumImage}
                        type="button"
                      >
                        <img src={AppPics.DeleteSvg} alt="Delete" />
                      </button>
                    )}
                </div> */}

                  <div className="col-lg-4 col-md-6 col-sm-12">
                    <AppUploadBlock
                      labelProps={{ label: 'Podcast Image' }}
                      props={{
                        referenceKey: 'PODCAST_IMAGE',
                        isImage: true,
                        isPublic: true,
                        uploadCallback: setDisableSubmit,
                        handleChange: ({ file, uploadedData }) => {
                          setPodcastInfo((p) => ({
                            ...p,
                            podcastImage: uploadedData.file,
                            podcastImageFullPath: `${uploadedData.basePath}${uploadedData.file}`,
                          }));
                          setNewlyUploadedImages((prev) => ({
                            ...prev,
                            podcastImage: true,
                          }));
                          setDeletedImages((prev) => ({
                            ...prev,
                            podcastImage: false,
                          }));
                        },
                        fileData: {
                          fullPath: deletedImages.podcastImage
                            ? null
                            : podcastInfo.podcastImageFullPath,
                        },
                        clearFile: deletedImages.podcastImage,
                        tabIndex: 8,
                      }}
                    />
                    {(podcastInfo.podcastImage || podcastInfo.podcastImageFullPath) &&
                      !deletedImages.podcastImage && (
                        <button
                          className="actions-icons mx-2 text-danger mt-2"
                          title="Delete Podcast Image"
                          onClick={handleDeletePodcastImageConfirm}
                          type="button"
                        >
                          <img src={AppPics.DeleteSvg} alt="Delete" />
                        </button>
                      )}
                  </div>

                  <div className="col-lg-4 col-md-6 col-sm-12">
                    <PodcastCategoryDropdown
                      ref={dropdownRef}
                      value={podcastInfo.podcastCategory}
                      handleChange={handleCategoryChange}
                      onKeyDown={handleDropdownKeyDown}
                      onFocus={(e) => {
                        const selectElement = e.target.querySelector('select') || e.target;
                        if (selectElement.tagName === 'SELECT') {
                          selectElement.focus();
                        }
                      }}
                      tabIndex={9}
                      options={categories}
                      isLoading={isCategoryLoading}
                      isError={isCategoryError}
                      version="v2"
                      defaultLook={false}
                      fieldErrors={errors['podcastCategory']}
                    />
                    {errors.podcastCategory && (
                      <div className="text-danger">{errors.podcastCategory.errorMessage}</div>
                    )}
                  </div>
                </div>

                <div className="row mt-4 d-flex justify-content-end">
                  <AppButton
                    type="submit"
                    variantClass="btn-primary mx-3"
                    style={{ minWidth: '1.5rem' }}
                    onClick={() => handlePublishToggleConfirm(!published)}
                    disabled={!isSaved || isLoading}
                    tabIndex={6}
                  >
                    {published ? 'Unpublish Podcast' : 'Publish Podcast'}
                  </AppButton>
                  <AppButton
                    variantClass="btn-primary mx-3"
                    style={{ minWidth: '1.5rem' }}
                    onClick={save}
                    disabled={disableSubmit || isLoading}
                    tabIndex={10}
                  >
                    {isLoading ? 'Saving...' : 'Save Podcast'}
                  </AppButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const podcastInfoSchema = Joi.object({
  podcastName: Joi.string().trim().min(1).max(100).required().messages({
    'string.base': 'Podcast name must be a string.',
    'string.empty': 'Podcast name cannot be empty.',
    'string.max': 'Podcast name must be at most 100 characters long.',
    'any.required': 'Podcast name is required.',
  }),

  podcastCategory: Joi.string().trim().required().messages({
    'string.base': 'Podcast category must be a string.',
    'string.empty': 'Please select a podcast category.',
    'any.required': 'Podcast category is required.',
  }),
});

export default ManagePodcast;
